import React, { useState } from 'react';
import {
  ShieldCheck, FileText, CheckCircle, XCircle,
  AlertTriangle, Lock, ArrowRight, BarChart3,
  Eye, ChevronDown, ChevronUp, Info, Download, Brain
} from 'lucide-react';

// Brand colors
export const BRAND_BLUE = '#2B4B6A';
export const BRAND_ORANGE = '#F58A5A';

export const GRADE_CONFIG = {
  A: { color: 'from-green-400 to-green-600', bg: 'bg-green-50', text: 'text-green-700', label: 'Excellent', desc: 'This LOI meets all compliance requirements.' },
  B: { color: 'from-blue-400 to-blue-600', bg: 'bg-blue-50', text: 'text-blue-700', label: 'Good', desc: 'This LOI passes compliance with minor concerns.' },
  C: { color: 'from-yellow-400 to-yellow-600', bg: 'bg-yellow-50', text: 'text-yellow-700', label: 'Fair', desc: 'This LOI has several issues and may not meet compliance standards.' },
  D: { color: 'from-orange-400 to-orange-600', bg: 'bg-orange-50', text: 'text-orange-700', label: 'Poor', desc: 'This LOI has major issues. This may not be the original LOI. Exercise caution.' },
  E: { color: 'from-red-500 to-red-700', bg: 'bg-red-50', text: 'text-red-700', label: 'Critical', desc: 'Serious red flags detected. This is likely not the original LOI or may contain significant irregularities.' },
};

export const CATEGORY_ORDER = ['content', 'metadata', 'revision', 'stamp', 'consistency', 'xmp', 'forensic'];
export const CATEGORY_LABELS = {
  content: { label: 'Content Fields', icon: FileText, desc: 'Required LOI fields and information' },
  metadata: { label: 'Document Metadata', icon: Info, desc: 'PDF creation and software analysis' },
  revision: { label: 'Edit History', icon: AlertTriangle, desc: 'How many times this document was modified' },
  stamp: { label: 'Stamps & Images', icon: ShieldCheck, desc: 'Company stamps, seals, and logos' },
  consistency: { label: 'Consistency', icon: Eye, desc: 'Cross-referencing data within the document' },
  xmp: { label: 'Deep Metadata', icon: BarChart3, desc: 'Extended metadata analysis' },
  forensic: { label: 'Forensic Analysis', icon: ShieldCheck, desc: 'Hidden data and security checks' },
  intel: { label: 'AI Intelligence', icon: Brain, desc: 'AI-powered document analysis and risk assessment' },
};

export const DownloadButton = ({ onClick, large }) => (
  <button
    onClick={onClick}
    className={`flex items-center gap-2 bg-gradient-to-r from-blue-500 to-cyan-500 text-white rounded-xl font-medium hover:from-blue-600 hover:to-cyan-600 transition shadow-sm ${
      large ? 'px-8 py-3 text-sm' : 'px-5 py-2.5 text-sm'
    }`}
  >
    <Download className="w-4 h-4" />
    Download PDF Report
  </button>
);

export const GradeHeroCard = ({ result }) => {
  const grade = result.summary?.grade || '?';
  const config = GRADE_CONFIG[grade] || GRADE_CONFIG.C;
  const score = result.summary?.score || 0;

  return (
    <div className={`${config.bg} rounded-2xl p-8 border shadow-sm`}>
      <div className="flex items-center gap-6">
        <div className={`w-24 h-24 rounded-full bg-gradient-to-br ${config.color} flex items-center justify-center shadow-lg flex-shrink-0`}>
          <span className="text-4xl font-black text-white">{grade}</span>
        </div>
        <div className="flex-1">
          <h2 className={`text-2xl font-bold ${config.text}`}>
            {config.label}
          </h2>
          <p className="text-gray-600 mt-1">{config.desc}</p>
          <div className="flex items-center gap-4 mt-3">
            <div>
              <span className="text-3xl font-bold text-gray-900">{score}%</span>
              <span className="text-sm text-gray-500 ml-1">score</span>
            </div>
            <div className="text-sm text-gray-500">
              {result.summary?.passed || 0} of {result.summary?.total_checks || 0} checks passed
            </div>
          </div>
          <div className="w-full h-3 rounded-full bg-gray-200 mt-3 overflow-hidden">
            <div
              className={`h-full rounded-full bg-gradient-to-r ${config.color} transition-all duration-1000`}
              style={{ width: `${score}%` }}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export const ScoreCard = ({ label, value, color }) => {
  if (!value) return null;
  const rate = value.rate || 0;
  const colorMap = {
    red: { bg: 'bg-red-50', text: 'text-red-600', bar: 'bg-red-500', valText: 'text-red-900' },
    orange: { bg: 'bg-orange-50', text: 'text-orange-600', bar: 'bg-orange-500', valText: 'text-orange-900' },
    blue: { bg: 'bg-blue-50', text: 'text-blue-600', bar: 'bg-blue-500', valText: 'text-blue-900' },
  };
  const c = colorMap[color] || colorMap.blue;

  return (
    <div className={`${c.bg} rounded-xl p-4 border border-gray-200 shadow-sm`}>
      <p className={`text-xs ${c.text} font-medium mb-1`}>{label}</p>
      <p className={`text-2xl font-bold ${c.valText}`}>{value.passed}/{value.total}</p>
      <div className="w-full h-1.5 rounded-full bg-gray-200 mt-2 overflow-hidden">
        <div className={`h-full rounded-full ${c.bar}`} style={{ width: `${rate}%` }} />
      </div>
      <p className="text-xs text-gray-400 mt-1">{rate}% pass rate</p>
    </div>
  );
};

export const IntelAnalysisBox = ({ intel, isPaidUser = false }) => {
  const [expanded, setExpanded] = useState(false);
  const status = intel.status || (intel.sections ? 'success' : 'unknown');
  const sections = intel.sections || {};
  const hasRedFlags = sections.red_flags?.length > 0 && sections.red_flags[0] !== 'No red flags detected.';
  const isMasked = intel.masked === true;

  // Non-success states: disabled or error
  if (status === 'disabled' || status === 'error') {
    return (
      <div className="bg-white border border-gray-200 rounded-xl overflow-hidden shadow-sm">
        <div className="flex items-center gap-3 p-4">
          <Brain className="w-5 h-5 text-gray-300 flex-shrink-0" />
          <div className="flex-1 text-left">
            <p className="text-gray-400 font-medium text-sm">Trados Intel Analysis</p>
            <p className="text-gray-300 text-xs">
              {status === 'disabled' ? 'AI analysis is not enabled' : (intel.reason || 'AI analysis encountered an error')}
            </p>
          </div>
          <span className="text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-400 font-medium">
            {status === 'disabled' ? 'Disabled' : 'Unavailable'}
          </span>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white border border-purple-200 rounded-xl overflow-hidden shadow-sm">
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center gap-3 p-4 hover:bg-purple-50/50 transition"
      >
        <Brain className="w-5 h-5 text-purple-500 flex-shrink-0" />
        <div className="flex-1 text-left">
          <p className="text-gray-900 font-medium text-sm">Trados Intel Analysis</p>
          <p className="text-gray-400 text-xs">AI-powered document analysis and risk assessment</p>
        </div>
        {hasRedFlags && (
          <span className="text-xs px-2 py-0.5 rounded-full bg-red-100 text-red-600 font-medium">
            {sections.red_flags.length} flag{sections.red_flags.length > 1 ? 's' : ''}
          </span>
        )}
        {isMasked && <Lock className="w-4 h-4 text-purple-400" />}
        {expanded ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
      </button>
      {expanded && (
        <div className="border-t border-purple-100 p-4 space-y-4">
          {/* Overall Assessment */}
          {sections.overall_assessment && (
            <div className="bg-purple-50 rounded-lg p-3">
              <p className="text-sm text-gray-700 leading-relaxed">{sections.overall_assessment}</p>
            </div>
          )}

          {/* Red Flags */}
          {hasRedFlags && (
            <div>
              <h4 className="text-sm font-semibold text-red-600 mb-2 flex items-center gap-1.5">
                <AlertTriangle className="w-4 h-4" /> Red Flags
              </h4>
              <div className="space-y-1">
                {sections.red_flags.map((flag, i) => (
                  <div key={i} className="flex items-start gap-2 text-sm bg-red-50 rounded-lg px-3 py-2">
                    <XCircle className="w-4 h-4 text-red-400 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-700">{flag}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Positive Indicators */}
          {sections.positive_indicators?.length > 0 && (
            <div>
              <h4 className="text-sm font-semibold text-green-600 mb-2 flex items-center gap-1.5">
                <CheckCircle className="w-4 h-4" /> Positive Indicators
              </h4>
              <div className="space-y-1">
                {sections.positive_indicators.map((item, i) => (
                  <div key={i} className="flex items-start gap-2 text-sm bg-green-50 rounded-lg px-3 py-2">
                    <CheckCircle className="w-4 h-4 text-green-400 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-700">{item}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Recommendations */}
          {sections.recommendations?.length > 0 && (
            <div>
              <h4 className="text-sm font-semibold text-blue-600 mb-2 flex items-center gap-1.5">
                <Info className="w-4 h-4" /> Recommendations
              </h4>
              <div className="space-y-1">
                {sections.recommendations.map((rec, i) => (
                  <div key={i} className="flex items-start gap-2 text-sm bg-blue-50 rounded-lg px-3 py-2">
                    <ArrowRight className="w-4 h-4 text-blue-400 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-700">{rec}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Upgrade CTA for masked (non-paid) users */}
          {isMasked && (
            <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 flex flex-col items-center text-center">
              <Lock className="w-6 h-6 text-purple-400 mb-2" />
              <p className="text-sm text-gray-700 font-semibold mb-1">Full Intelligence Report</p>
              <p className="text-xs text-gray-500 mb-3">Available to paid customers only</p>
              <a
                href="/register"
                className="inline-flex items-center gap-2 px-5 py-2 bg-purple-600 text-white rounded-xl font-medium hover:bg-purple-700 transition text-sm"
              >
                Get Started <ArrowRight className="w-4 h-4" />
              </a>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export const FullReport = ({ result, isPaidUser = false }) => {
  const checks = result.checks || [];
  const [expandedCategories, setExpandedCategories] = useState(new Set());

  const checksByCategory = {};
  checks.forEach(c => {
    if (!checksByCategory[c.category]) checksByCategory[c.category] = [];
    checksByCategory[c.category].push(c);
  });

  const availableCategories = CATEGORY_ORDER.filter(cat => checksByCategory[cat]?.length > 0);
  const allExpanded = availableCategories.length > 0 && availableCategories.every(cat => expandedCategories.has(cat));

  const toggleExpandAll = () => {
    if (allExpanded) {
      setExpandedCategories(new Set());
    } else {
      setExpandedCategories(new Set(availableCategories));
    }
  };

  const toggleCategory = (cat) => {
    setExpandedCategories(prev => {
      const next = new Set(prev);
      if (next.has(cat)) next.delete(cat);
      else next.add(cat);
      return next;
    });
  };

  return (
    <div className="mt-8 space-y-3">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-bold text-gray-900">Detailed Analysis</h3>
        <button
          onClick={toggleExpandAll}
          className="inline-flex items-center gap-1.5 px-4 py-1.5 rounded-full text-sm font-medium text-white shadow-sm hover:opacity-90 transition"
          style={{ backgroundColor: '#7be382' }}
        >
          {allExpanded ? (
            <><ChevronUp className="w-4 h-4" /> Collapse All</>
          ) : (
            <><ChevronDown className="w-4 h-4" /> Expand All</>
          )}
        </button>
      </div>

      {result.contact_info && (
        <div className="bg-white border border-gray-200 rounded-xl p-4 shadow-sm">
          <h4 className="text-gray-900 font-semibold mb-2">Extracted Contact Information</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm">
            {result.contact_info.emails?.length > 0 && (
              <div className="text-gray-700"><span className="text-gray-400">Emails:</span> {result.contact_info.emails.join(', ')}</div>
            )}
            {result.contact_info.domains?.length > 0 && (
              <div className="text-gray-700"><span className="text-gray-400">Domains:</span> {result.contact_info.domains.join(', ')}</div>
            )}
            {result.contact_info.phones?.length > 0 && (
              <div className="text-gray-700"><span className="text-gray-400">Phones:</span> {result.contact_info.phones.slice(0, 5).join(', ')}</div>
            )}
            {result.contact_info.registration_numbers?.length > 0 && (
              <div className="text-gray-700"><span className="text-gray-400">Reg #:</span> {result.contact_info.registration_numbers.join(', ')}</div>
            )}
          </div>
        </div>
      )}

      {/* Trados Intel Analysis — First */}
      {result.intel_analysis && (
        <>
          <IntelAnalysisBox intel={result.intel_analysis} />
          {!result.intel_analysis.masked && (
            <div className="bg-gray-50 border border-gray-200 rounded-xl p-4 text-xs text-gray-400 leading-relaxed">
              <span className="font-semibold text-gray-500">Disclaimer:</span> This AI-generated analysis is provided for informational purposes only and does not constitute legal, financial, or professional advice. The analysis is produced by an automated system and may contain inaccuracies. Users should independently verify all information and consult qualified professionals before making decisions based on this report. Trados shall not be held liable for any losses, damages, or claims arising from the use of or reliance on this AI-generated content.
            </div>
          )}
        </>
      )}

      {CATEGORY_ORDER.map(cat => {
        const catChecks = checksByCategory[cat];
        if (!catChecks || catChecks.length === 0) return null;
        const catInfo = CATEGORY_LABELS[cat] || { label: cat, desc: '' };
        const CatIcon = catInfo.icon || ShieldCheck;
        const passedCount = catChecks.filter(c => c.passed).length;
        const isExpanded = expandedCategories.has(cat);

        return (
          <div key={cat} className="bg-white border border-gray-200 rounded-xl overflow-hidden shadow-sm">
            <button
              onClick={() => toggleCategory(cat)}
              className="w-full flex items-center gap-3 p-4 hover:bg-gray-50 transition"
            >
              <CatIcon className="w-5 h-5 text-blue-500 flex-shrink-0" />
              <div className="flex-1 text-left">
                <p className="text-gray-900 font-medium text-sm">{catInfo.label}</p>
                <p className="text-gray-400 text-xs">{catInfo.desc}</p>
              </div>
              <span className="text-sm font-medium text-gray-500">
                {passedCount}/{catChecks.length}
              </span>
              <div className="w-16 h-1.5 rounded-full bg-gray-200 overflow-hidden">
                <div
                  className={`h-full rounded-full ${passedCount === catChecks.length ? 'bg-green-500' : passedCount > catChecks.length / 2 ? 'bg-yellow-500' : 'bg-red-500'}`}
                  style={{ width: `${(passedCount / catChecks.length) * 100}%` }}
                />
              </div>
              {isExpanded ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
            </button>
            {isExpanded && (
              <div className="border-t border-gray-100 divide-y divide-gray-100">
                {catChecks.map((check, idx) => (
                  <div key={idx} className="flex items-start gap-3 px-4 py-3">
                    {check.passed ? (
                      <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                    ) : check.severity === 'critical' ? (
                      <XCircle className="w-4 h-4 text-red-500 mt-0.5 flex-shrink-0" />
                    ) : (
                      <AlertTriangle className="w-4 h-4 text-yellow-500 mt-0.5 flex-shrink-0" />
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-gray-800">{check.label}</p>
                      <p className="text-xs text-gray-400 mt-0.5">{check.detail}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      })}

    </div>
  );
};

export const LockedCategories = ({ result }) => {
  const checks = result.checks || [];
  const checksByCategory = {};
  checks.forEach(c => {
    if (!checksByCategory[c.category]) checksByCategory[c.category] = [];
    checksByCategory[c.category].push(c);
  });

  return (
    <div className="mt-8 space-y-3">
      <h3 className="text-lg font-bold text-gray-900 mb-4">Detailed Analysis</h3>
      {CATEGORY_ORDER.map(cat => {
        const catChecks = checksByCategory[cat];
        if (!catChecks || catChecks.length === 0) return null;
        const catInfo = CATEGORY_LABELS[cat] || { label: cat, desc: '' };
        const CatIcon = catInfo.icon || ShieldCheck;
        const passedCount = catChecks.filter(c => c.passed).length;

        return (
          <div key={cat} className="bg-white border border-gray-200 rounded-xl overflow-hidden shadow-sm opacity-75">
            <div className="w-full flex items-center gap-3 p-4 cursor-not-allowed">
              <CatIcon className="w-5 h-5 text-blue-500 flex-shrink-0" />
              <div className="flex-1 text-left">
                <p className="text-gray-900 font-medium text-sm">{catInfo.label}</p>
                <p className="text-gray-400 text-xs">{catInfo.desc}</p>
              </div>
              <span className="text-sm font-medium text-gray-500">
                {passedCount}/{catChecks.length}
              </span>
              <div className="w-16 h-1.5 rounded-full bg-gray-200 overflow-hidden">
                <div
                  className={`h-full rounded-full ${passedCount === catChecks.length ? 'bg-green-500' : passedCount > catChecks.length / 2 ? 'bg-yellow-500' : 'bg-red-500'}`}
                  style={{ width: `${(passedCount / catChecks.length) * 100}%` }}
                />
              </div>
              <Lock className="w-4 h-4 text-gray-300" />
            </div>
          </div>
        );
      })}
    </div>
  );
};

export const LockedIntelTeaser = ({ intel, message }) => {
  const sections = intel?.sections || {};
  const flagCount = intel?.red_flag_count ?? (sections.red_flags?.length || 0);
  const hasRedFlags = flagCount > 0 && sections.red_flags?.[0] !== 'No red flags detected.';
  const status = intel?.status || (intel?.sections ? 'success' : 'unknown');

  if (status === 'disabled' || status === 'error') return null;

  return (
    <div className="bg-white border border-purple-200 rounded-xl overflow-hidden shadow-sm">
      <div className="flex items-center gap-3 p-4">
        <Brain className="w-5 h-5 text-purple-500 flex-shrink-0" />
        <div className="flex-1 text-left">
          <p className="text-gray-900 font-medium text-sm">Trados Intel Analysis</p>
          <p className="text-gray-400 text-xs">AI-powered document analysis and risk assessment</p>
        </div>
        {hasRedFlags && (
          <span className="text-xs px-2 py-0.5 rounded-full bg-red-100 text-red-600 font-medium">
            {flagCount} flag{flagCount > 1 ? 's' : ''}
          </span>
        )}
        <Lock className="w-4 h-4 text-gray-300" />
      </div>
      <div className="border-t border-purple-100 bg-purple-50/50 p-6 text-center">
        <Lock className="w-8 h-8 text-purple-300 mx-auto mb-2" />
        <p className="text-sm text-gray-600 font-medium">{message}</p>
      </div>
    </div>
  );
};
